import { useState } from "react"
import { Link } from "@tanstack/react-router"
import { ArrowRight, Check, RotateCcw, X } from "lucide-react"

import { Button } from "@/components/ui/button"
import { ConfettiBurst } from "@/components/confetti-burst"
import { useCoachThreadApi } from "@/lib/coach-thread-api"
import { usePruefungChat } from "@/lib/pruefung-chat"
import { useVerlaengerePruefungsziel } from "@/lib/queries"
import { cn } from "@/lib/utils"
import {
  PRUEFUNG_ZIEL_VERLAENGERUNGEN,
  type PruefungsAbschluss,
  type PruefungsAbschlussSchicht,
  type PruefungsZielErgebnis,
} from "@nl/shared"

/**
 * The result once an area is done in this conversation.
 *
 * Its own card rather than a variant of the progress card: that one counts
 * everything ever answered, this one only the run that is meant to pass.
 */
export function PruefungsabschlussCard({
  abschluss,
  feiern,
}: {
  abschluss: PruefungsAbschluss
  /** Only on a fresh finish, never after a reload. */
  feiern: boolean
}) {
  const thread = useCoachThreadApi()
  if (abschluss.ziel) {
    return (
      <ZielAbschluss
        abschluss={abschluss}
        ziel={abschluss.ziel}
        feiern={feiern}
      />
    )
  }
  return <SchichtAbschluss abschluss={abschluss} feiern={feiern} thread={thread} />
}

function SchichtAbschluss({
  abschluss,
  feiern,
  thread,
}: {
  abschluss: PruefungsAbschluss
  feiern: boolean
  thread: ReturnType<typeof useCoachThreadApi>
}) {
  const beendet = abschluss.schichten.find(
    (s) => s.slug === abschluss.lernfeldSlug,
  )
  const [konfetti, setKonfetti] = useState(feiern && beendet?.bestanden === true)
  const naechste = abschluss.schichten.find((s) => s.bestanden === null)

  return (
    <div className="rounded-xl border border-[var(--rule)] bg-card p-4">
      {konfetti && <ConfettiBurst onDone={() => setKonfetti(false)} />}

      <div className="eyebrow text-muted-foreground">
        {beendet ? "Schicht abgeschlossen" : "Durchgang abgeschlossen"}
      </div>

      {beendet ? (
        <Ergebnis
          erfolg={beendet.bestanden === true}
          titel={`${beendet.titel}: ${beendet.bestanden ? "bestanden" : "noch nicht bestanden"}`}
          richtig={beendet.richtig}
          beantwortet={beendet.beantwortet}
          quote={beendet.quote}
          grenze={abschluss.grenze}
          grenzeLabel="bestanden ab"
        />
      ) : (
        <p className="mt-1.5 text-[14px] leading-relaxed">
          Du hast alle Schichten durchgearbeitet. Bestanden ab{" "}
          <span className="tnum">{abschluss.grenze} %</span> je Schicht.
        </p>
      )}

      <div className="mt-3 divide-y divide-[var(--rule)] border-y border-[var(--rule)]">
        {abschluss.schichten.map((schicht) => (
          <Zeile
            key={schicht.slug}
            schicht={schicht}
            grenze={abschluss.grenze}
            aktuell={schicht.slug === beendet?.slug}
          />
        ))}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {naechste && (
          <Button
            type="button"
            size="sm"
            className="gap-1.5"
            onClick={() =>
              thread?.ask(`Frag mich jetzt zu „${naechste.titel}“ ab.`)
            }
          >
            Weiter mit {naechste.titel}
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        )}
        {/* A repeat needs a new thread: questions already asked stay excluded
            for the whole conversation, so this one has nothing left to ask. */}
        {beendet?.bestanden === false && (
          <Button asChild size="sm" variant="outline" className="gap-1.5">
            <Link
              to="/pruefung"
              search={{ lernfeld: beendet.slug }}
            >
              <RotateCcw className="h-3.5 w-3.5" />
              {beendet.titel} neu starten
            </Link>
          </Button>
        )}
      </div>
    </div>
  )
}

/**
 * The result of a run with a question target.
 *
 * "Prüfungsniveau" instead of "bestanden": a run of ten questions does not
 * cover an area, so it cannot pass one. Areas that were fully covered along
 * the way still show their verdict in the rows.
 */
function ZielAbschluss({
  abschluss,
  ziel,
  feiern,
}: {
  abschluss: PruefungsAbschluss
  ziel: PruefungsZielErgebnis
  feiern: boolean
}) {
  const thread = useCoachThreadApi()
  const chat = usePruefungChat()
  const verlaengern = useVerlaengerePruefungsziel(chat?.threadId)
  const [konfetti, setKonfetti] = useState(feiern && ziel.erreicht)
  // Once used, the buttons stay down: the run continues below this card.
  const [verlaengert, setVerlaengert] = useState(false)
  const gefragt = abschluss.schichten.filter((s) => s.beantwortet > 0)

  async function weiter(anzahl: number) {
    setVerlaengert(true)
    try {
      await verlaengern.mutateAsync({ anzahl })
    } catch {
      setVerlaengert(false)
      return
    }
    thread?.ask(`Weiter, noch ${anzahl} Fragen.`)
  }

  return (
    <div className="rounded-xl border border-[var(--rule)] bg-card p-4">
      {konfetti && <ConfettiBurst onDone={() => setKonfetti(false)} />}

      <div className="eyebrow text-muted-foreground">
        {ziel.beantwortet >= ziel.anzahl
          ? "Lernziel erreicht"
          : "Alle Fragen durchgearbeitet"}
      </div>

      <Ergebnis
        erfolg={ziel.erreicht}
        titel={
          ziel.erreicht ? "Prüfungsniveau erreicht" : "noch unter Prüfungsniveau"
        }
        richtig={ziel.richtig}
        beantwortet={ziel.beantwortet}
        quote={ziel.quote}
        grenze={abschluss.grenze}
        grenzeLabel="Prüfungsniveau ab"
      />

      {gefragt.length > 0 && (
        <div className="mt-3 divide-y divide-[var(--rule)] border-y border-[var(--rule)]">
          {gefragt.map((schicht) => (
            <Zeile
              key={schicht.slug}
              schicht={schicht}
              grenze={abschluss.grenze}
              aktuell={false}
            />
          ))}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {ziel.weitereFragen &&
          PRUEFUNG_ZIEL_VERLAENGERUNGEN.map((anzahl, i) => (
            <Button
              key={anzahl}
              type="button"
              size="sm"
              variant={i === 0 ? "default" : "outline"}
              className="tnum"
              disabled={verlaengert || !chat?.threadId}
              onClick={() => void weiter(anzahl)}
            >
              +{anzahl} Fragen
            </Button>
          ))}
        <Button asChild size="sm" variant="outline" className="gap-1.5">
          <Link
            to="/pruefung"
            search={
              abschluss.lernfeldSlug
                ? { lernfeld: abschluss.lernfeldSlug }
                : { gemischt: true }
            }
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Neue Runde
          </Link>
        </Button>
      </div>
    </div>
  )
}

function Ergebnis({
  erfolg,
  titel,
  richtig,
  beantwortet,
  quote,
  grenze,
  grenzeLabel,
}: {
  erfolg: boolean
  titel: string
  richtig: number
  beantwortet: number
  quote: number | null
  grenze: number
  grenzeLabel: string
}) {
  return (
    <div className="mt-2 flex items-start gap-3">
      <span
        className={cn(
          "mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full",
          erfolg
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-muted-foreground",
        )}
      >
        {erfolg ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
      </span>
      <div className="min-w-0">
        <p className="text-[17px] font-semibold leading-snug">{titel}</p>
        <p className="mt-0.5 text-[13px] text-muted-foreground">
          <span className="tnum font-medium text-foreground">
            {richtig} von {beantwortet}
          </span>{" "}
          richtig ·{" "}
          <span className="tnum font-medium text-foreground">
            {quote ?? 0} %
          </span>{" "}
          · {grenzeLabel} <span className="tnum">{grenze} %</span>
        </p>
      </div>
    </div>
  )
}

function Zeile({
  schicht,
  grenze,
  aktuell,
}: {
  schicht: PruefungsAbschlussSchicht
  grenze: number
  aktuell: boolean
}) {
  return (
    <div className="py-2.5">
      <div className="flex items-baseline justify-between gap-3 text-[13px]">
        <span className={cn("min-w-0 truncate", aktuell && "font-medium")}>
          {schicht.titel}
        </span>
        <span className="flex shrink-0 items-baseline gap-2">
          <span className="tnum text-muted-foreground">
            {schicht.beantwortet}/{schicht.gesamt}
            {schicht.quote !== null && ` · ${schicht.quote} %`}
          </span>
          <Status bestanden={schicht.bestanden} />
        </span>
      </div>
      {/* The bar shows the hit rate, not progress: the tick at the pass mark
          is what the advisor compares against. */}
      <div className="relative mt-2">
        <div className="h-1 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={cn(
              "h-full rounded-full",
              schicht.bestanden === false ? "bg-muted-foreground/60" : "bg-primary",
            )}
            style={{ width: `${schicht.quote ?? 0}%` }}
          />
        </div>
        <div
          aria-hidden
          className="absolute -top-0.5 h-2 w-px bg-foreground/50"
          style={{ left: `${grenze}%` }}
        />
      </div>
    </div>
  )
}

function Status({ bestanden }: { bestanden: boolean | null }) {
  if (bestanden === null) {
    return <span className="text-[12px] text-muted-foreground">offen</span>
  }
  return (
    <span
      className={cn(
        "text-[12px] font-medium",
        bestanden ? "text-primary" : "text-muted-foreground",
      )}
    >
      {bestanden ? "Bestanden" : "Nicht bestanden"}
    </span>
  )
}
